import {
  LegacyOfflineStored,
  offlineStoreReference,
} from '@app/shared/old-frontend/data-access';
import { EntityId } from '@fmnts/api';
import {
  IApiNextLocationCheckInCreateDto,
  NextLocation,
} from '@fmnts/api/next';
import {
  AsyncData,
  EntityAsyncUpload,
  createAsyncDataAdapter,
  createEntityAsyncUploadAdapter,
  maybeLoadedAsyncData,
} from '@fmnts/common/store';
import { InlineTranslateable } from '@fmnts/i18n';
import {
  GeneralHttpError,
  LegacyFmntsApiError,
} from '@formunauts/shared/errors/domain';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { produce } from 'immer';
import { LocationCheckIn } from './next-location.types';
import {
  NextLocationApiActions,
  NextLocationCheckInOfflineActions,
  NextLocationCheckInOfflineUploadActions,
  NextLocationDetailPageActions,
  NextLocationInitActions,
  NextLocationListPageActions,
} from './next-locations.actions';

export const NEXT_LOCATIONS_FEATURE_KEY = 'nextLocations';

export type LocationOfflineStored =
  LegacyOfflineStored<IApiNextLocationCheckInCreateDto>;

export interface NextLocationsFeatureState extends EntityState<NextLocation> {
  /** Active location check in from today. */
  checkIn: LocationCheckIn | null;
  /**
   * Check ins persisted offline that could not be uploaded
   */
  persistedCheckIns: EntityAsyncUpload<LocationOfflineStored>;
  today: AsyncData<readonly EntityId[], InlineTranslateable>;
}

export interface NextLocationsPartialState {
  readonly [NEXT_LOCATIONS_FEATURE_KEY]: NextLocationsFeatureState;
}
export const asyncUploadEntityAdapter =
  createEntityAsyncUploadAdapter<LocationOfflineStored>({
    selectId: offlineStoreReference,
  });

const asyncLocationsAdapter = createAsyncDataAdapter<
  readonly EntityId[],
  InlineTranslateable
>();
export const nextLocationsAdapter = createEntityAdapter<NextLocation>();

export const initialNextLocationFeatureState: NextLocationsFeatureState =
  nextLocationsAdapter.getInitialState({
    checkIn: null,
    persistedCheckIns: asyncUploadEntityAdapter.getInitialState(),
    today: asyncLocationsAdapter.getInitialState(),
  });

export const nextLocationsFeature = createFeature({
  name: NEXT_LOCATIONS_FEATURE_KEY,
  reducer: createReducer(
    initialNextLocationFeatureState,
    on(
      NextLocationInitActions.hydrate,
      (state, { locationId, timestamp }): NextLocationsFeatureState => ({
        ...state,
        checkIn: {
          locationId,
          timestamp,
        },
      }),
    ),
    on(
      NextLocationApiActions.getLocation.success,
      (state, { payload: location }): NextLocationsFeatureState =>
        nextLocationsAdapter.setOne(location, state),
    ),
    on(
      NextLocationApiActions.getLocationList.request,
      NextLocationApiActions.getLocationListAll.request,
      (state): NextLocationsFeatureState => ({
        ...state,
        today: asyncLocationsAdapter.setLoading(state.today),
      }),
    ),
    on(
      NextLocationApiActions.getLocationList.success,
      (state, { payload: { results: locations } }): NextLocationsFeatureState =>
        nextLocationsAdapter.upsertMany([...locations], {
          ...state,
          today: asyncLocationsAdapter.setData(
            [
              ...(maybeLoadedAsyncData(state.today)?.data ?? []),
              ...locations.map((location) => location.id),
            ],
            state.today,
          ),
        }),
    ),
    on(
      NextLocationApiActions.getLocationListAll.success,
      (state, { payload }): NextLocationsFeatureState =>
        nextLocationsAdapter.upsertMany([...payload], {
          ...state,
          today: asyncLocationsAdapter.setData(
            payload.map((location) => location.id),
            state.today,
          ),
        }),
    ),
    on(
      NextLocationApiActions.getLocationList.failure,
      NextLocationApiActions.getLocationListAll.failure,
      (state, { error }): NextLocationsFeatureState => ({
        ...state,
        today: asyncLocationsAdapter.setError(error.message, state.today),
      }),
    ),
    on(
      NextLocationListPageActions.checkIn,
      NextLocationDetailPageActions.checkIn,
      (state, { locationId, timestamp }): NextLocationsFeatureState => ({
        ...state,
        checkIn: {
          locationId,
          timestamp,
        },
      }),
    ),
    on(
      NextLocationCheckInOfflineActions.hydrate,
      produce(
        (state, { locations }): NextLocationsFeatureState => ({
          ...state,
          persistedCheckIns: asyncUploadEntityAdapter.setMany(
            locations,
            state.persistedCheckIns,
          ),
        }),
      ),
    ),
    on(
      NextLocationCheckInOfflineActions.persist,
      produce(
        (state, { checkIn }): NextLocationsFeatureState => ({
          ...state,
          persistedCheckIns: asyncUploadEntityAdapter.addOne(
            {
              id: checkIn.fundraiser,
              timestamp: `${new Date(checkIn.createtime).getTime()}`,
              category: 'locationselect',
              payload: checkIn,
            },
            state.persistedCheckIns,
          ),
        }),
      ),
    ),
    on(
      NextLocationCheckInOfflineUploadActions.request,
      produce((draft, { dataKey }) => {
        draft.persistedCheckIns = asyncUploadEntityAdapter.setLoading(
          dataKey,
          draft.persistedCheckIns,
        );
      }),
    ),
    on(
      NextLocationCheckInOfflineUploadActions.failure,
      produce((draft, { dataKey, error }) => {
        draft.persistedCheckIns = asyncUploadEntityAdapter.setError(
          dataKey,
          extractMsg(error),
          draft.persistedCheckIns,
        );
      }),
    ),
    on(
      NextLocationCheckInOfflineUploadActions.success,
      produce((draft, { response: { dataKey } }) => {
        draft.persistedCheckIns = asyncUploadEntityAdapter.removeOne(
          dataKey,
          draft.persistedCheckIns,
        );
      }),
    ),
    on(
      NextLocationListPageActions.reset,
      (): NextLocationsFeatureState => initialNextLocationFeatureState,
    ),
  ),
});

export const { reducer: nextLocationsFeatureReducer } = nextLocationsFeature;

function extractMsg(error: GeneralHttpError): InlineTranslateable {
  if (error.httpError.status === 0) {
    return 'network_error';
  }
  const apiErrorDetails =
    ((error.error as LegacyFmntsApiError)?.details as InlineTranslateable) ??
    null;

  return apiErrorDetails ?? 'internal_server_error';
}
